import { marked, type Token, type Tokens } from "marked";
import type { Content, ContentText, CustomTableLayout, Margins, TDocumentDefinitions } from "pdfmake/interfaces";
import { type FlavourId, flavourOptions } from "./flavours";
import { MARGIN, MEASURE } from "./paper";
import { drawnSize, fetchPictures, type Pictures } from "./pictures";

// The document typeset here rather than printed: what a print pipeline hands back is the pipeline's own decision, and
// what came back was a picture of the page. This writes the PDF itself, so the text in it is text wherever it is made.
export async function pdfDocument(text: string, flavour: FlavourId, title: string): Promise<Blob> {
  const [pdfMake, definition] = await Promise.all([writer(), pdfDefinition(text, flavour, title)]);
  return pdfMake.createPdf(definition).getBlob();
}

// What pdfmake is handed, which is the whole of the typesetting and none of the writing, so a test can read it.
export async function pdfDefinition(text: string, flavour: FlavourId, title: string): Promise<TDocumentDefinitions> {
  const tokens = marked.lexer(text, flavourOptions(flavour));
  const pictures = await figures(await fetchPictures(tokens, RASTERS));

  return {
    info: { title },
    pageSize: "A4",
    pageMargins: [MARGIN, MARGIN, MARGIN, MARGIN],
    // Roboto is the family pdfmake carries and the one this site is already set in, so a saved page and a saved
    // document are the same document twice; Courier is a standard face and costs the file nothing to name.
    defaultStyle: { font: "Roboto", fontSize: 10.5, lineHeight: 1.35, color: "#1a1b1e" },
    content: blocks(tokens, pictures),
  };
}

// Fetched when somebody asks for a PDF and never before: the writer and the four faces it embeds are two megabytes,
// which is a page nobody printing a document would have paid for otherwise.
async function writer() {
  const [module, roboto, courier] = await Promise.all([
    import("pdfmake/build/pdfmake"),
    import("pdfmake/build/fonts/Roboto"),
    import("pdfmake/build/standard-fonts/Courier"),
  ]);

  // The bundle is UMD and its types name the exports one by one, so the singleton is on `default` at run time and
  // the module object itself is what stands for it here.
  const pdfMake = (module as unknown as { default?: typeof module }).default ?? module;
  pdfMake.addFontContainer(roboto.default);
  pdfMake.addFontContainer(courier.default);

  return pdfMake;
}

function blocks(tokens: Token[], pictures: Figures): Content[] {
  return tokens.flatMap((token): Content[] => {
    switch (token.type) {
      case "heading":
        return [{
          text: runs(token.tokens, pictures),
          fontSize: HEADING_SIZES[token.depth - 1] ?? HEADING_SIZES.at(-1),
          bold: true,
          margin: [0, token.depth === 1 ? 0 : 12, 0, 6],
        }];

      case "paragraph":
        return paragraph(token.tokens, pictures);

      case "text": {
        const text = token as Tokens.Text;
        return text.tokens ? paragraph(text.tokens, pictures) : [{ text: text.text, margin: BLOCK_MARGIN }];
      }

      case "code":
        return [{
          table: {
            widths: ["*"],
            body: [[{ text: token.text, font: "Courier", fontSize: 9, preserveLeadingSpaces: true }]],
          },
          layout: CODE_RULE,
          margin: [0, 0, 0, 10],
        }];

      case "blockquote":
        return [{
          table: { widths: ["*"], body: [[{ stack: blocks(token.tokens ?? [], pictures) }]] },
          layout: QUOTE_RULE,
          margin: [0, 0, 0, 10],
        }];

      case "list":
        return [list(token as Tokens.List, pictures)];

      case "table":
        return [table(token as Tokens.Table, pictures)];

      case "hr":
        return [{
          canvas: [{ type: "line", x1: 0, y1: 0, x2: MEASURE, y2: 0, lineWidth: 1, lineColor: RULE }],
          margin: [0, 4, 0, 14],
        }];

      // A block of raw HTML is markup with no typesetting in it, and a tag spelled out on the page says less than
      // the gap it would leave; the preview and the saved page are where a document's own HTML is honoured.
      case "html":
      case "space":
        return [];

      default:
        return "tokens" in token && Array.isArray(token.tokens) ? blocks(token.tokens, pictures) : [];
    }
  });
}

function list(token: Tokens.List, pictures: Figures): Content {
  const items = token.items.map((item): Content => {
    // The gap between two paragraphs is the gap between two items when the paragraph is one, and one line of a list
    // wants far less air under it than a paragraph of prose does.
    const [first, ...rest] = blocks(item.tokens, pictures).map(tighten);
    if (!item.task) return { stack: [first, ...rest], margin: ITEM_MARGIN };

    // A box drawn in the letters everybody has, rather than in a glyph a face may be missing, and on the item's own
    // first line rather than above it.
    const box: ContentText = { text: item.checked ? "[x] " : "[ ] ", font: "Courier" };
    return { stack: [{ text: [box, ...opening(first)] }, ...rest], margin: ITEM_MARGIN };
  });

  return token.ordered
    ? { ol: items, start: token.start || 1, margin: BLOCK_MARGIN }
    : { ul: items, margin: BLOCK_MARGIN };
}

// A block inside a list item, given the air a line wants rather than the air a paragraph does.
function tighten(block: Content): Content {
  return isText(block) ? { ...block, margin: ITEM_MARGIN } : block;
}

function isText(block: Content | undefined): block is ContentText {
  return typeof block === "object" && !Array.isArray(block) && "text" in block;
}

// A paragraph is its runs of text, unless it carries pictures: those are blocks of their own, since a picture is not
// a letter and nothing lays one out in the middle of a line. Two beside each other stay beside each other, a row of
// badges being the way most documents that have any use them at all.
function paragraph(tokens: Token[] | undefined, pictures: Figures): Content[] {
  const content: Content[] = [];
  let run: Token[] = [];
  let row: Figure[] = [];

  const flushText = () => {
    if (run.length > 0) content.push({ text: runs(run, pictures), margin: BLOCK_MARGIN });
    run = [];
  };
  const flushRow = () => {
    if (row.length === 1) content.push({ ...row[0], margin: BLOCK_MARGIN });
    // Each in a column of its own width, since a column that sized itself to the picture inside it would be sizing
    // the picture instead.
    else if (row.length > 1) {
      content.push({
        columns: row.map((picture) => ({ width: "auto", stack: [picture] })),
        columnGap: 6,
        margin: BLOCK_MARGIN,
      });
    }
    row = [];
  };

  for (const token of tokens ?? []) {
    const picture = token.type === "image" ? pictures.get(token.href) : undefined;
    if (picture) {
      flushText();
      row.push(picture);
      continue;
    }
    // The space either side of a picture is the document's spacing and not a line of its own.
    if (token.type === "text" && token.raw.trim() === "") continue;
    flushRow();
    run.push(token);
  }

  flushText();
  flushRow();
  return content;
}

// The runs of an item's first paragraph, which is what a checkbox goes in front of.
function opening(block: Content | undefined): ContentText[] {
  if (!isText(block)) return [];
  return Array.isArray(block.text) ? block.text as ContentText[] : [{ text: String(block.text) }];
}

function table(token: Tokens.Table, pictures: Figures): Content {
  const align = token.align.map((alignment) => alignment ?? "left");
  const header = token.header.map((cell, column) => ({
    text: runs(cell.tokens, pictures),
    bold: true,
    alignment: align[column],
  }));
  const body = token.rows.map((row) =>
    row.map((cell, column) => ({ text: runs(cell.tokens, pictures), alignment: align[column] }))
  );

  return {
    table: { headerRows: 1, widths: token.header.map(() => "*"), body: [header, ...body] },
    layout: TABLE_RULES,
    margin: [0, 0, 0, 10],
  };
}

// The marks inside a line, each one a run of its own: pdfmake takes an array of them where a paragraph is one string.
function runs(tokens: Token[] | undefined, pictures: Figures, style: Style = {}): ContentText[] {
  if (!tokens) return [];

  return tokens.flatMap((token): ContentText[] => {
    switch (token.type) {
      case "strong":
        return runs(token.tokens, pictures, { ...style, bold: true });
      case "em":
        return runs(token.tokens, pictures, { ...style, italics: true });
      case "del":
        return runs(token.tokens, pictures, { ...style, decoration: "lineThrough" });
      case "codespan":
        return [{ ...style, text: token.text, font: "Courier", fontSize: 9.5 }];
      case "link":
        return runs(token.tokens, pictures, { ...style, link: token.href, color: LINK, decoration: "underline" });
      case "br":
        return [{ ...style, text: "\n" }];
      // A tag inside a line arrives on its own, apart from the words it wraps, so dropping it keeps them; a break is
      // the one tag that stands for something on the page, and two words run together are not what it wrote.
      case "html":
        return BREAK_TAG.test(token.raw) ? [{ ...style, text: "\n" }] : [];
      // A picture is not a run of text, so what stands in for one here is what the document says it shows.
      case "image":
        return [{ ...style, text: token.text || token.title || "", italics: true }];
      case "escape":
      case "text":
        return [{ ...style, text: token.text }];
      default:
        return "tokens" in token && Array.isArray(token.tokens)
          ? runs(token.tokens, pictures, style)
          : [{ ...style, text: "raw" in token ? String(token.raw) : "" }];
    }
  });
}

// pdfmake takes a raster as base64 with its type in front of it, and sizes a picture by its width alone.
async function figures(pictures: Pictures): Promise<Figures> {
  const drawn = await Promise.all([...pictures].map(async ([address, picture]) => {
    const { width } = drawnSize(picture);
    const figure: Figure = "svg" in picture
      ? { svg: picture.svg, width }
      : { image: await encode(picture.raster), width };
    return [address, figure] as const;
  }));

  return new Map(drawn);
}

function encode(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

type Figure = ({ image: string } | { svg: string }) & { width: number };

type Figures = Map<string, Figure>;

type Style = Omit<ContentText, "text">;

const BREAK_TAG = /^<br\s*\/?>$/i;

const HEADING_SIZES = [21, 16.5, 14, 12, 11, 10.5];

// The only two rasters pdfmake can embed.
const RASTERS = ["image/png", "image/jpeg"];

// The gap under a block, and the tighter one under a line of a list, a list being one block however many lines it is.
const BLOCK_MARGIN: Margins = [0, 0, 0, 8];
const ITEM_MARGIN: Margins = [0, 0, 0, 2];

const RULE = "#dee2e6";

// A block of code stands in a box of its own, a quotation behind one line down its left, and a table on its grid.
const CODE_RULE: CustomTableLayout = {
  hLineWidth: () => 1,
  vLineWidth: () => 1,
  hLineColor: () => RULE,
  vLineColor: () => RULE,
  paddingLeft: () => 8,
  paddingRight: () => 8,
  paddingTop: () => 6,
  paddingBottom: () => 6,
};

const QUOTE_RULE: CustomTableLayout = {
  hLineWidth: () => 0,
  vLineWidth: (index) => index === 0 ? 3 : 0,
  vLineColor: () => RULE,
  paddingLeft: () => 10,
  paddingRight: () => 0,
  paddingTop: () => 2,
  paddingBottom: () => 2,
};

const TABLE_RULES: CustomTableLayout = {
  hLineWidth: () => 1,
  vLineWidth: () => 1,
  hLineColor: () => RULE,
  vLineColor: () => RULE,
  paddingLeft: () => 6,
  paddingRight: () => 6,
  paddingTop: () => 4,
  paddingBottom: () => 4,
};
const LINK = "#1971c2";
